import { integrationsApi } from '@/api'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import type { IntegrationItem } from '@/api/integrations/integrations.types'
import { filterMaxCrmIntegrations } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/MaxCrmBotsSettings/lib/maxCrmIntegration.helpers'
import { filterTgCrmIntegrations } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/TgCrmBotsSettings/lib/tgCrmIntegration.helpers'
import { filterVkCrmIntegrations } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/VkCrmCommunitiesSettings/lib/vkCrmIntegration.helpers'
import { filterWazzupCrmIntegrations } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'
import {
  CRM_CHAT_PROVIDER_KEYS,
  parseCrmChatProviderKey,
} from '@pages/Admin/CRM/components/integrations/common/lib/crmChatProviderKey'
import { isIntegrationCrmAccessible } from './crmIntegrationAccess'
import {
  VK_INTEGRATION_LEAD_CHAT_UNAVAILABLE,
  type VkIntegrationLeadChatAvailability,
} from './vkIntegrationLeadChat'

export type CrmIntegrationChatProviderAvailability = {
  isConnected: boolean
  canUseLeadChat: boolean
}

export type CrmIntegrationChatAvailabilityMap = Record<
  CrmChatProviderKey,
  CrmIntegrationChatProviderAvailability
>

const CRM_PROVIDER_FILTERS: Record<
  CrmChatProviderKey,
  (items: IntegrationItem[] | null | undefined) => IntegrationItem[]
> = {
  vkontakte: filterVkCrmIntegrations,
  max: filterMaxCrmIntegrations,
  telegram: filterTgCrmIntegrations,
  wazzup: filterWazzupCrmIntegrations,
}

const EMPTY_PROVIDER: CrmIntegrationChatProviderAvailability = {
  isConnected: false,
  canUseLeadChat: false,
}

export const CRM_INTEGRATION_CHAT_AVAILABILITY_EMPTY: CrmIntegrationChatAvailabilityMap =
  {
    vkontakte: { ...EMPTY_PROVIDER },
    max: { ...EMPTY_PROVIDER },
    telegram: { ...EMPTY_PROVIDER },
    wazzup: { ...EMPTY_PROVIDER },
  }

const normalizeIntegrationProviderKey = (
  provider: string | null | undefined,
): CrmChatProviderKey | null => {
  if (provider === 'vk' || provider === 'vkontakte') {
    return 'vkontakte'
  }
  return parseCrmChatProviderKey(provider)
}

/**
 * GET /v1/integrations?usage=crm
 * Приходят все CRM-интеграции (в т.ч. менеджеру).
 * UI: status !== connected → «Не подключено» (вход закрыт);
 * connected && !crm_access.is_accessible → «Нет доступа» (вход есть, чаты с замком);
 * connected && accessible → обычная плитка / вход в чаты.
 */
const fetchCrmIntegrationsList = async (): Promise<IntegrationItem[]> => {
  try {
    const res = await integrationsApi.getAll({ usage: 'crm' })
    const raw = res?.data?.data ?? res?.data
    if (!Array.isArray(raw)) return []

    return raw.filter((item) => {
      const provider = normalizeIntegrationProviderKey(item?.provider)
      if (!provider) return false
      return CRM_PROVIDER_FILTERS[provider]([item]).length > 0
    })
  } catch {
    return []
  }
}

/**
 * Подключённость CRM-чатов:
 * один источник — GET /integrations?usage=crm.
 * Legacy test-connection и unread-count для плиток не используем.
 */
export type CrmIntegrationChatAvailabilityFetchResult = {
  providers: CrmIntegrationChatAvailabilityMap
  vkLeadChat: VkIntegrationLeadChatAvailability
  connectedIntegrations: IntegrationItem[]
}

export const fetchCrmIntegrationChatAvailability =
  async (): Promise<CrmIntegrationChatAvailabilityFetchResult> => {
    const integrationsList = await fetchCrmIntegrationsList()

    const connectedIntegrations = integrationsList.filter(
      (item) => item.status === 'connected',
    )
    const accessibleIntegrations = connectedIntegrations.filter((item) =>
      isIntegrationCrmAccessible(item),
    )

    const providers = { ...CRM_INTEGRATION_CHAT_AVAILABILITY_EMPTY }

    CRM_CHAT_PROVIDER_KEYS.forEach((provider) => {
      const listConnected = connectedIntegrations.some(
        (item) => normalizeIntegrationProviderKey(item.provider) === provider,
      )
      const listAccessible = accessibleIntegrations.some(
        (item) => normalizeIntegrationProviderKey(item.provider) === provider,
      )

      providers[provider] = {
        isConnected: listConnected,
        canUseLeadChat: listAccessible,
      }
    })

    const vkLeadChat: VkIntegrationLeadChatAvailability = providers.vkontakte
      .isConnected
      ? {
          isConfigured: true,
          isActive: true,
          canUseLeadChat: providers.vkontakte.canUseLeadChat,
        }
      : VK_INTEGRATION_LEAD_CHAT_UNAVAILABLE

    return {
      providers,
      vkLeadChat,
      connectedIntegrations,
    }
  }

export const hasAnyCrmIntegrationChatConnected = (
  map: CrmIntegrationChatAvailabilityMap,
): boolean => CRM_CHAT_PROVIDER_KEYS.some((provider) => map[provider].canUseLeadChat)
